// ----------------------------------------------------------------------

export function remToPx(value) {
  return Math.round(parseFloat(value) * 16);
}

export function pxToRem(value) {
  return `${value / 16}rem`;
}

export function responsiveFontSizes({ sm, md, lg }) {
  return {
    '@media (min-width:600px)': {
      fontSize: pxToRem(sm),
    },
    '@media (min-width:900px)': {
      fontSize: pxToRem(md),
    },
    '@media (min-width:1200px)': {
      fontSize: pxToRem(lg),
    },
  };
}

// ----------------------------------------------------------------------

const FONT_PRIMARY = "'Montserrat', sans-serif";

const typography = {
  fontFamily: FONT_PRIMARY,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  
  // Heading variants - consistent structure with responsive sizes
  h1: {
    fontWeight: 800,
    lineHeight: 1.25, // 80/64
    fontSize: pxToRem(40),
    ...responsiveFontSizes({ sm: 52, md: 58, lg: 64 }),
  },
  h2: {
    fontWeight: 800,
    lineHeight: 1.33, // 64/48
    fontSize: pxToRem(32),
    ...responsiveFontSizes({ sm: 40, md: 44, lg: 48 }),
  },
  h3: {
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: pxToRem(24),
    ...responsiveFontSizes({ sm: 24, md: 28, lg: 32 }),
  },
  h4: {
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: pxToRem(20),
    ...responsiveFontSizes({ sm: 20, md: 22, lg: 24 }),
  },
  h5: {
    fontWeight: 600,
    lineHeight: 1.5,
    fontSize: pxToRem(18),
    ...responsiveFontSizes({ sm: 16, md: 18, lg: 20 }),
  },
  h6: {
    fontWeight: 600,
    lineHeight: 1.56, // 28/18
    fontSize: pxToRem(16),
    ...responsiveFontSizes({ sm: 14, md: 16, lg: 18 }),
  },
  
  // Body text variants
  subtitle1: {
    fontWeight: 600,
    lineHeight: 1.5,
    fontSize: pxToRem(16),
  },
  subtitle2: {
    fontWeight: 500,
    lineHeight: 1.43, // 20/14
    fontSize: pxToRem(14),
    letterSpacing: '0.2px',
  },
  body1: {
    fontWeight: 400,
    lineHeight: 1.5,
    fontSize: pxToRem(16),
  },
  body2: {
    fontWeight: 400,
    lineHeight: 1.57, // 22/14
    fontSize: pxToRem(14),
  },
  caption: {
    fontWeight: 400,
    lineHeight: 1.5,
    fontSize: pxToRem(12),
  },
  overline: {
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: pxToRem(12),
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  button: {
    fontWeight: 700,
    lineHeight: 1.71, // 24/14
    fontSize: pxToRem(14),
    textTransform: 'capitalize',
  },
  
  // Custom variants - kept for backward compatibility
  // Note: Colors should be set via sx prop for theme compatibility
  mainTitles: {
    fontWeight: 700,
    fontFamily: FONT_PRIMARY,
    lineHeight: 1.5,
    fontSize: pxToRem(32),
    display: 'block',
    ...responsiveFontSizes({ sm: 25, md: 28, lg: 32 }),
    // Color should be set via sx prop: color: 'text.titles'
  },
  secondaryTitles: {
    fontWeight: 600,
    fontFamily: FONT_PRIMARY,
    lineHeight: 3.5, // 70/20
    fontSize: pxToRem(20),
    display: 'block',
    ...responsiveFontSizes({ sm: 16, md: 18, lg: 20 }),
    // Color should be set via sx prop: color: 'text.primary'
  },
  subtitles: {
    fontWeight: 500,
    lineHeight: 1.5,
    fontSize: pxToRem(13),
    // Color should be set via sx prop: color: 'text.secondary'
  },
  form: {
    fontWeight: 700,
    fontFamily: "'Roboto', sans-serif",
    lineHeight: 0.86, // 12/14
    fontSize: pxToRem(14),
    letterSpacing: '0.15px',
    // Color should be set via sx prop: color: 'text.secondary'
  },
  validation: {
    fontWeight: 400,
    lineHeight: 1.5,
    fontSize: pxToRem(12),
    // Color should be set via sx prop: color: 'error.main'
  },
  error: {
    fontWeight: 600,
    fontFamily: FONT_PRIMARY,
    lineHeight: 3.5, // 70/20
    fontSize: pxToRem(20),
    display: 'block',
    // Color should be set via sx prop: color: 'error.main'
  },
  email: {
    fontWeight: 600,
    fontFamily: FONT_PRIMARY,
    lineHeight: 1.5,
    fontSize: pxToRem(10),
    display: 'flex',
    alignItems: 'center',
    // Color should be set via sx prop: color: 'primary.main'
  },
  specs: {
    fontWeight: 600,
    fontFamily: FONT_PRIMARY,
    lineHeight: 1.5,
    fontSize: pxToRem(12),
    display: 'block',
    // Color should be set via sx prop: color: 'text.primary'
  },
  smallText: {
    fontWeight: 500,
    fontFamily: FONT_PRIMARY,
    lineHeight: 1.5,
    fontSize: pxToRem(10),
    // Color should be set via sx prop: color: 'text.secondary'
  },
  normal: {
    fontWeight: 500,
    fontFamily: FONT_PRIMARY,
    lineHeight: 2.95, // 65/22
    fontSize: pxToRem(22),
    ...responsiveFontSizes({ sm: 18, md: 20, lg: 22 }),
    // Color should be set via sx prop: color: 'text.primary'
  },
  large: {
    fontWeight: 500,
    lineHeight: 1.5,
    fontSize: pxToRem(40),
    ...responsiveFontSizes({ sm: 32, md: 36, lg: 40 }),
    // Color should be set via sx prop: color: 'text.primary'
  },
};

export default typography;
